import { apiClient, API_ENDPOINTS } from './api';
import { cacheService } from './cacheService';

class CourseEventService {
  // Флаги для защиты от дублирования запросов
  _isLoadingEvents = new Map();
  _eventsPromises = new Map();
  
  /**
   * Получить мероприятия для конкретного курса
   * @param {number} courseId - ID курса
   * @param {boolean} forceRefresh - принудительное обновление кеша
   * @returns {Promise<Object>} - данные курса и список мероприятий
   */
  async getCourseEvents(courseId, forceRefresh = false) {
    const cacheKey = cacheService.getCacheKey(`course_events_${courseId}`);
    
    if (!forceRefresh) {
      // Увеличили TTL до 10 минут для мероприятий (было 2 минуты)
      const cached = cacheService.get(cacheKey, 10 * 60 * 1000);
      if (cached) {
        console.log(`Возвращаем кешированные данные для курса ${courseId}`);
        return cached;
      }
    }
    
    // Защита от дублирования запросов для одного курса
    if (this._isLoadingEvents.get(courseId) && !forceRefresh) {
      console.log(`Ожидание завершения загрузки мероприятий для курса ${courseId}...`);
      return this._eventsPromises.get(courseId);
    }
    
    this._isLoadingEvents.set(courseId, true);
    
    const promise = this._fetchCourseEvents(courseId, forceRefresh);
    this._eventsPromises.set(courseId, promise);
    
    try {
      const result = await promise;
      return result;
    } finally {
      this._isLoadingEvents.set(courseId, false);
      this._eventsPromises.delete(courseId);
    }
  }
  
  async _fetchCourseEvents(courseId, forceRefresh = false) {
    try {
      console.log(`Загрузка мероприятий для курса ${courseId}...`);
      const response = await apiClient.get(API_ENDPOINTS.COURSE_EVENTS(courseId));
      
      const normalizedData = this.normalizeResponse(response, courseId);
      const cacheKey = cacheService.getCacheKey(`course_events_${courseId}`);
      cacheService.set(cacheKey, normalizedData);
      
      return normalizedData;
    } catch (error) {
      console.error(`Ошибка загрузки мероприятий курса ${courseId}:`, error);
      throw error;
    }
  }
  
  /**
   * Нормализация ответа сервера
   * @param {Object} data - данные от сервера
   * @param {number} courseId - ID курса (для fallback)
   * @returns {Object} - нормализованные данные
   */
  normalizeResponse(data, courseId = null) {
    // Если данные уже в нужном формате
    if (data && data.course && data.events) {
      return {
        course: {
          id: data.course.id,
          name: data.course.name,
          category: data.course.category
        },
        events: (data.events || []).map(event => ({
          id: event.id,
          title: event.title,
          format: event.format,
          formatLabel: event.format_label || this.getFormatLabel(event.format),
          startDate: event.start_date,
          endDate: event.end_date,
          location: event.location || '',
          trainingCenter: event.training_center || '',
          status: event.status,
          statusLabel: event.status_label || this.getStatusLabel(event.status),
          statusColor: event.status_color,
          cost: parseFloat(event.cost) || 0,
          participantsCount: event.participants_count || 0,
          maxParticipants: event.max_participants || 0,
          availableSlots: event.available_slots !== undefined 
            ? event.available_slots 
            : ((event.max_participants || 0) - (event.participants_count || 0)),
          participants: event.participants || []
        })),
        statistics: data.statistics || {
          totalEvents: (data.events || []).length,
          upcomingEvents: (data.events || []).filter(e => new Date(e.start_date) > new Date()).length,
          totalParticipants: (data.events || []).reduce((sum, e) => sum + (e.participants_count || 0), 0)
        },
        total: data.total || (data.events || []).length || 0
      };
    }
    
    // Если данные в обернутом виде { data: {...} }
    if (data && data.data) {
      return this.normalizeResponse(data.data, courseId);
    }
    
    // Обработка прямого массива мероприятий
    if (Array.isArray(data)) {
      return {
        course: { id: courseId, name: null, category: null },
        events: data.map(event => ({
          id: event.id,
          title: event.title || 'Без названия',
          format: event.format || 'onsite',
          formatLabel: event.format_label || this.getFormatLabel(event.format),
          startDate: event.start_date,
          endDate: event.end_date,
          location: event.location || '',
          trainingCenter: event.training_center || '',
          status: event.status || 'planned',
          statusLabel: event.status_label || this.getStatusLabel(event.status),
          statusColor: event.status_color,
          cost: parseFloat(event.cost) || 0,
          participantsCount: event.participants_count || 0,
          maxParticipants: event.max_participants || 0,
          availableSlots: event.available_slots || ((event.max_participants || 0) - (event.participants_count || 0)),
          participants: event.participants || []
        })),
        statistics: {
          totalEvents: data.length,
          upcomingEvents: data.filter(e => new Date(e.start_date) > new Date()).length,
          totalParticipants: data.reduce((sum, e) => sum + (e.participants_count || 0), 0)
        },
        total: data.length
      };
    }
    
    console.warn('Неожиданный формат ответа для мероприятий курса:', data);
    return {
      course: { id: courseId, name: null, category: null },
      events: [],
      statistics: { totalEvents: 0, upcomingEvents: 0, totalParticipants: 0 },
      total: 0
    };
  }
  
  /**
   * Получить предстоящие мероприятия (с возможностью записи)
   * @param {number} courseId - ID курса
   * @returns {Promise<Array>} - массив предстоящих мероприятий
   */
  async getUpcomingEvents(courseId) {
  const data = await this.getCourseEvents(courseId);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return data.events
    .filter(event => {
      if (!event.startDate) return false;
      
      const eventDate = new Date(event.startDate);
      // Сравниваем только год, месяц и день
      const eventDateOnly = new Date(
        eventDate.getFullYear(),
        eventDate.getMonth(),
        eventDate.getDate()
      );
      
      // Для сегодняшней даты используем строгое >=
      const isTodayOrFuture = eventDateOnly >= today;
      const hasSlots = (event.availableSlots || 0) > 0;
      
      return isTodayOrFuture && hasSlots;
    })
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
}
  
  /**
   * Проверить, есть ли доступные мероприятия для записи
   * @param {number} courseId - ID курса
   * @returns {Promise<boolean>} - true если есть доступные мероприятия
   */
  async hasAvailableEvents(courseId) {
    const upcoming = await this.getUpcomingEvents(courseId);
    return upcoming.length > 0;
  }
  
  /**
   * Инвалидация кеша для курса
   * @param {number} courseId - ID курса
   */
  invalidateCache(courseId) {
    cacheService.invalidate(`course_events_${courseId}`);
  }
  
  /**
   * Получить метку формата
   */
  getFormatLabel(format) {
    const labels = {
      'onsite': 'Очное',
      'online': 'Онлайн',
      'hybrid': 'Гибридный'
    };
    return labels[format] || format;
  }
  
  /**
   * Получить метку статуса
   */
  getStatusLabel(status) {
    const labels = {
      'draft': 'Черновик',
      'planned': 'Запланировано',
      'confirmed': 'Подтверждено',
      'in_progress': 'В процессе',
      'completed': 'Завершено',
      'cancelled': 'Отменено'
    };
    return labels[status] || status;
  }
}

export const courseEventService = new CourseEventService();